/* ─── A1CW PDP scripts ─────────────────────────────────────
   Gallery, variant pills (Web Component), tabs, add-to-cart popup.
   ─────────────────────────────────────────── */

(function () {
  'use strict';

  // ═══ Gallery ══════════════════════════════════════
  document.querySelectorAll('[data-pdp-gallery]').forEach(function (gallery) {
    const slides = gallery.querySelectorAll('[data-gallery-slide]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!slides.length || !thumbs.length) return;

    function showSlide(targetId) {
      slides.forEach(function (s) {
        const match = s.dataset.mediaId === String(targetId);
        s.hidden = !match;
        s.classList.toggle('is-active', match);
      });
      thumbs.forEach(function (t) {
        const match = t.dataset.target === String(targetId);
        t.classList.toggle('is-active', match);
        t.setAttribute('aria-selected', match ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { showSlide(thumb.dataset.target); });
    });

    // expose so variant changes can switch slides
    gallery.showSlide = showSlide;
  });

  // ═══ Variant pills (Web Component) ═══════════════════
  class VariantPills extends HTMLElement {
    connectedCallback() {
      try {
        const json = this.querySelector('[data-variants-json]');
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        console.error('VariantPills: invalid variants JSON', e);
        this.variants = [];
      }
      this.pills = Array.from(this.querySelectorAll('.pdp-pill'));
      this.hiddenInput = this.querySelector('[data-variant-id-input]');
      this.selected = this.readCurrentSelection();
      this.pills.forEach(p => p.addEventListener('click', this.onPillClick.bind(this)));
      this.applyDisabledStates();
    }

    readCurrentSelection() {
      const groups = this.querySelectorAll('.pdp-variants__group');
      const selection = [];
      groups.forEach(group => {
        const pos = parseInt(group.dataset.optionPosition, 10) - 1;
        const checked = group.querySelector('.pdp-pill[aria-checked="true"]');
        selection[pos] = checked ? checked.dataset.optionValue : null;
      });
      return selection;
    }

    onPillClick(e) {
      const pill = e.currentTarget;
      if (pill.getAttribute('aria-disabled') === 'true') return;
      const pos = parseInt(pill.dataset.optionPosition, 10) - 1;
      const val = pill.dataset.optionValue;

      const group = pill.closest('.pdp-variants__group');
      group.querySelectorAll('.pdp-pill').forEach(p => p.setAttribute('aria-checked', 'false'));
      pill.setAttribute('aria-checked', 'true');

      const legend = group.querySelector('[data-selected-for]');
      if (legend) legend.textContent = val;

      this.selected[pos] = val;
      this.applyDisabledStates();
      this.resolveVariant();
    }

    applyDisabledStates() {
      this.pills.forEach(pill => {
        const pos = parseInt(pill.dataset.optionPosition, 10) - 1;
        const val = pill.dataset.optionValue;
        const hypothetical = this.selected.slice();
        hypothetical[pos] = val;
        const match = this.variants.find(v => v.options.every(function (opt, i) {
          return hypothetical[i] == null || opt === hypothetical[i];
        }));
        const available = match && match.available;
        pill.setAttribute('aria-disabled', available ? 'false' : 'true');
      });
    }

    resolveVariant() {
      const match = this.variants.find(v => v.options.every((opt, i) => opt === this.selected[i]));
      if (!match) return;
      if (this.hiddenInput) this.hiddenInput.value = match.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: match } }));
    }
  }
  if (!customElements.get('variant-pills')) customElements.define('variant-pills', VariantPills);

  // ═══ Variant change → update price, stock, gallery ══
  function formatMoney(cents) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  document.addEventListener('variant:change', function (e) {
    const variant = e.detail.variant;
    if (!variant) return;

    // Price — when the selected variant has no price (0 or null), render the
    // "Contact For Price Quote" link instead of $0.00. Wrapped in a .price
    // span so theme styling and the cfpq-watcher keep working.
    const priceEl = document.querySelector('[data-variant-price]');
    if (priceEl) {
      if (variant.price == null || variant.price === 0) {
        priceEl.innerHTML = '<span class="price tabular"><a href="/pages/contact-us" class="price__cfpq">Contact For Price Quote</a></span>';
      } else {
        let inner = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          inner = '<s>' + formatMoney(variant.compare_at_price) + '</s> ' + inner;
        }
        priceEl.innerHTML = '<span class="price tabular">' + inner + '</span>';
      }
    }

    // SKU
    const skuWrap = document.querySelector('[data-variant-sku]');
    const skuEl   = skuWrap ? skuWrap.querySelector('.pdp-sku__val') : null;
    if (skuWrap && skuEl) {
      if (variant.sku) {
        skuEl.textContent = variant.sku;
        skuWrap.hidden = false;
      } else {
        skuWrap.hidden = true;
      }
    }

    // Stock indicator suppressed — A1CW dropships, inventory flag unreliable.
    // Add to Cart stays enabled regardless of variant.available.

    // Quote modal data-variant-id sync
    document.querySelectorAll('[data-open-quote-modal]').forEach(function (t) {
      t.dataset.variantId = variant.id;
    });

    // Gallery — switch slide
    if (variant.featured_media_id) {
      const gallery = document.querySelector('[data-pdp-gallery]');
      if (gallery && typeof gallery.showSlide === 'function') {
        gallery.showSlide(variant.featured_media_id);
      }
    }
  });

  // ═══ Description clamp toggle ══════════════════════
  document.querySelectorAll('[data-description-toggle]').forEach(function (toggle) {
    const body = document.querySelector('[data-description-body]');
    if (!body) return;
    toggle.addEventListener('click', function () {
      const clamped = body.classList.toggle('is-clamped');
      toggle.textContent = clamped ? 'Read full description' : 'Show less';
    });
  });

  // ═══ Tabs ═══════════════════════════════════════
  document.querySelectorAll('[data-pdp-tabs]').forEach(function (tabs) {
    const buttons = tabs.querySelectorAll('[data-tab-target]');
    const panels  = tabs.querySelectorAll('[data-tab-panel]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const target = btn.dataset.tabTarget;
        buttons.forEach(b => b.setAttribute('aria-selected', b === btn ? 'true' : 'false'));
        panels.forEach(p => { p.hidden = p.dataset.tabPanel !== target; });
      });
    });
  });

  // ═══ Qty +/- buttons ════════════════════════════
  document.querySelectorAll('[data-qty-decrement]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const input = btn.parentElement.querySelector('input[type="number"]');
      if (input) { input.stepDown(); input.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  });
  document.querySelectorAll('[data-qty-increment]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const input = btn.parentElement.querySelector('input[type="number"]');
      if (input) { input.stepUp(); input.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  });

  // ═══ Add to cart → AJAX + confirmation popup (stay on page) ══
  const pdpForm = document.getElementById('pdp-form');
  if (pdpForm) {
    pdpForm.addEventListener('submit', function (e) {
      e.preventDefault();
      const btn = pdpForm.querySelector('[data-add-to-cart-btn]');
      const label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(pdpForm)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, data: j }; }); })
        .then(function (res) {
          if (btn) { btn.disabled = false; btn.textContent = label; }
          if (!res.ok) {
            showCartNotice(null, null, (res.data && (res.data.description || res.data.message)) || 'Could not add to cart.');
            return;
          }
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) { showCartNotice(res.data, cart, null); });
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = label; }
        });
    });
  }

  function showCartNotice(item, cart, errorMsg) {
    const old = document.getElementById('a1-cart-notify');
    if (old) old.remove();
    const el = document.createElement('div');
    el.id = 'a1-cart-notify';
    el.className = 'cart-notify' + (errorMsg ? ' cart-notify--error' : '');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-live', 'polite');

    if (errorMsg) {
      el.innerHTML =
        '<div class="cart-notify__head"><strong>' + errorMsg + '</strong>' +
        '<button type="button" class="cart-notify__close" aria-label="Close">&times;</button></div>';
    } else {
      const img = item.image ? '<img src="' + item.image + '" alt="" class="cart-notify__img">' : '';
      const count = cart ? cart.item_count : item.quantity;
      el.innerHTML =
        '<div class="cart-notify__head"><span class="cart-notify__check" aria-hidden="true">✓</span>' +
        '<strong>Added to cart</strong>' +
        '<button type="button" class="cart-notify__close" aria-label="Close">&times;</button></div>' +
        '<div class="cart-notify__body">' + img +
        '<div class="cart-notify__info"><span class="cart-notify__title">' + (item.product_title || item.title) + '</span>' +
        '<span class="cart-notify__meta">Qty ' + item.quantity + '</span></div></div>' +
        '<div class="cart-notify__actions">' +
        '<a class="btn btn--primary btn--block" href="/checkout">Check out</a>' +
        '<a class="btn btn--secondary btn--block" href="/cart">View cart (' + count + ')</a>' +
        '<button type="button" class="btn btn--ghost btn--block" data-cart-continue>Continue shopping</button>' +
        '</div>';
      document.querySelectorAll('[data-cart-count]').forEach(function (n) { n.textContent = count; });
    }

    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-open'); });

    function close() {
      el.classList.remove('is-open');
      setTimeout(function () { if (el.parentNode) el.remove(); }, 250);
    }
    const closeBtn = el.querySelector('.cart-notify__close');
    if (closeBtn) closeBtn.addEventListener('click', close);
    const cont = el.querySelector('[data-cart-continue]');
    if (cont) cont.addEventListener('click', close);

    clearTimeout(window.__a1CartTimer);
    window.__a1CartTimer = setTimeout(close, 7000);
  }
})();
